import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams } from 'react-router';

import { createApiClient } from '@/api/client';
import { createLegacyKuduToken, getKuduUserId } from '@/api/legacyKuduAuth';
import { Button } from '@/components/Button/Button';
import { Card } from '@/components/Card/Card';
import { ErrorScreen } from '@/components/ErrorScreen';
import { LoadingScreen } from '@/components/LoadingScreen';
import { createProfileService } from '@/features/profile/profileService';
import type { ProfileData } from '@/features/profile/profileTypes';
import { profileQueryKey } from '@/features/profile/profileTypes';
import { withReturnTo } from '@/routes/returnTo';

import { useAuth } from './useAuth';
import styles from './CourseAccessRoute.module.css';

interface CourseAccessRouteProps {
  children: ReactNode;
}

function getCurrentReturnTo(location: ReturnType<typeof useLocation>) {
  return `${location.pathname}${location.search}${location.hash}`;
}

export function hasCourseAccess(
  courseIds: string[],
  courseId: string | null | undefined,
) {
  return Boolean(courseId && courseIds.includes(courseId));
}

// Stays until the user leaves. It used to return them to their courses after
// three seconds, read or not (WCAG 2.2.1, issue #112).
function CourseAccessDenied() {
  const navigate = useNavigate();

  return (
    <main className={styles.shell}>
      <Card className={styles.card} role="alert" variant="elevated">
        <h1 className={styles.title}>Course unavailable</h1>
        <p className={styles.message}>
          You don&apos;t have access to this course.
        </p>
        <div className={styles.actions}>
          <Button
            onClick={() => navigate('/app/courses', { replace: true })}
            variant="primary"
          >
            Go to my courses
          </Button>
        </div>
      </Card>
    </main>
  );
}

export function CourseAccessRoute({ children }: CourseAccessRouteProps) {
  const { courseId } = useParams();
  const { getIdToken, loading, signOut, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const kuduUserId = getKuduUserId(user) ?? user?.uid ?? null;
  const returnTo = getCurrentReturnTo(location);

  const profileQuery = useQuery<ProfileData, Error>({
    enabled: !loading && Boolean(user) && Boolean(kuduUserId),
    queryFn: async () => {
      if (!user || !kuduUserId) {
        throw new Error('You must be signed in to view this course.');
      }

      const client = createApiClient({
        baseUrl: import.meta.env.VITE_API_BASE_URL || '',
        getIdToken,
        getLegacyToken: async () => createLegacyKuduToken(user),
        onAuthError: async () => {
          await signOut();
          navigate(withReturnTo('/login', returnTo), { replace: true });
        },
      });

      return createProfileService(client).getProfile({ userId: kuduUserId });
    },
    queryKey: profileQueryKey(kuduUserId),
    // A refusal stays until the person leaves, so it is never decided on a copy
    // read before this page opened: a course made or joined since is not in it,
    // however recent the copy (issue #171). A copy that lists the course goes by
    // the usual staleness, so opening a course costs no extra read.
    refetchOnMount: (query) =>
      hasCourseAccess(query.state.data?.courseIds ?? [], courseId)
        ? true
        : 'always',
    retry: false,
  });

  const profile = profileQuery.data ?? null;
  const isAuthorized = hasCourseAccess(profile?.courseIds ?? [], courseId);

  if (loading || (Boolean(kuduUserId) && profileQuery.isPending)) {
    return <LoadingScreen message="Checking course access..." />;
  }

  if (profileQuery.error) {
    return (
      <ErrorScreen
        message="Unable to verify your course access."
        onRetry={() => {
          void profileQuery.refetch();
        }}
      />
    );
  }

  if (isAuthorized) {
    return <>{children}</>;
  }

  if (profileQuery.isFetching) {
    return <LoadingScreen message="Checking course access..." />;
  }

  return <CourseAccessDenied />;
}
